/**
 * Shaping the projection into what the Portfolio screen shows — ROADMAP 6.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GROUP IS UNDERLYING + EXPIRY, BECAUSE THAT IS WHAT A PAYOFF IS VALID FOR
 *
 * The obvious grouping is by strategy, and 5.13 already does it in the
 * drawer. It is the wrong unit HERE, for a reason that is about the maths
 * rather than taste: a payoff curve plots value against ONE underlying
 * settling at ONE expiry. Put a NIFTY leg and a BANKNIFTY leg on the same
 * axis and the x axis means two different things at once; put two expiries
 * on it and the near one has already settled while the far one has not.
 *
 * So a group is every position sharing an underlying and an expiry. Within
 * it a payoff is exact; across groups the P&L still adds up, because money
 * does, and that is all the totals claim.
 *
 * RUNNING AND SQUARED OFF ARE A PROPERTY OF THE ROW, NOT A SEPARATE FETCH
 *
 * The projection keeps a closed position's row — it carries the realised
 * P&L, which is the whole point of booking it. `netQty === 0` is what
 * "squared off" means, and nothing else needs to know.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { legMtm, netPnl, type MtmLeg } from '@apna/shared'

/**
 * One row of `GET /portfolio/positions` — a TRADE LEG since 7.10.
 *
 * It used to be a `Position`: the broker's grain, which nets two baskets on
 * one strike into a single row. Two baskets are now two rows, each with its
 * own entry price, P&L and trade — which is what the user placed.
 */
export interface ProjectedPosition {
  /** Stable row identity. The trade leg, not a broker position key. */
  tradeLegId: string
  tradeId: string
  /** `OPENING | OPEN | ISSUE | CLOSING | CLOSED | NEEDS_REVIEW`. */
  tradeStatus: string
  /** `PENDING | FILLED | FAILED | CLOSING | CLOSED`. */
  legStatus: string
  token: number | null
  tradingSymbol: string | null
  netQty: number
  /** UNITS intended at placement. What a FAILED leg would have been. */
  intendedQty: number
  avgPrice: number
  /** GROSS, per 6.4. */
  realisedPnl: number
  chargesTotal: number
  fillCount: number
  underlying: string | null
  strike: number | null
  optionType: string | null
  expiry: string | null
  lotSize: number | null
  /** 7.8. Units the broker stopped reporting; null when nothing is flagged. */
  orphanedQty: number | null
}

export type PortfolioTab = 'RUNNING' | 'SQUARED_OFF' | 'OVERALL'
export type ResultFilter = 'ALL' | 'WINNERS' | 'LOSERS'

export interface PositionRow extends ProjectedPosition {
  /** Null when the leg is open and unpriced. */
  readonly unrealised: number | null
  /** `realised + unrealised - charges`. Null when unrealised is. */
  readonly net: number | null
  readonly open: boolean
}

export interface PortfolioGroup {
  /** `<underlying>|<expiry>` — the unit a payoff is valid for. */
  readonly key: string
  readonly underlying: string
  readonly expiry: string | null
  /** `NIFTY Short Straddle`, or just the underlying when the shape is not one. */
  readonly title: string
  /** `07 AUG`, or `No expiry`. */
  readonly expiryLabel: string
  readonly rows: readonly PositionRow[]
  /** GROSS realised across the group. Always a complete number. */
  readonly realised: number
  readonly charges: number
  /** Covers the MARKED rows only; `unmarked` says how many it leaves out. */
  readonly unrealised: number
  readonly marked: number
  readonly unmarked: number
  /** Net of what is known. Null when nothing open could be priced. */
  readonly net: number | null
  readonly openRows: number
}

export const UNKNOWN_UNDERLYING = 'UNKNOWN'

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/** `07 AUG`, the way an expiry is read on a desk. */
export function expiryLabelOf(expiry: string | null): string {
  if (expiry === null) return 'No expiry'
  const d = new Date(expiry)
  if (Number.isNaN(d.getTime())) return 'No expiry'
  // UTC parts: an expiry is a DATE, and reading it in local time moves it a
  // day for anyone west of IST.
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`
}

/**
 * Name the structure, when it is unambiguously one.
 *
 * Derived from the legs, never stored — the same rule the strategy glyph
 * follows. A stored name can disagree with the position it claims to
 * describe once a leg is closed, and the name is the first thing read.
 *
 * ONLY SHAPES WITH ONE READING ARE NAMED. Two short legs at one strike is a
 * short straddle and nothing else; four legs in two spreads is a condor. A
 * shape this cannot place returns null and the caller falls back to the
 * underlying alone, because a confidently wrong name on a risk screen is
 * worse than no name.
 */
export function structureNameOf(rows: readonly PositionRow[]): string | null {
  const open = rows.filter((r) => r.open && r.strike !== null && r.optionType !== null)
  if (open.length === 0 || open.length !== rows.filter((r) => r.open).length) return null

  const calls = open.filter((r) => r.optionType === 'CE')
  const puts = open.filter((r) => r.optionType === 'PE')
  const allShort = open.every((r) => r.netQty < 0)
  const allLong = open.every((r) => r.netQty > 0)
  const strikes = new Set(open.map((r) => r.strike as number))

  if (open.length === 1) {
    const r = open[0]!
    const side = r.netQty < 0 ? 'Short' : 'Long'
    return `${side} ${r.optionType === 'CE' ? 'Call' : 'Put'}`
  }

  if (open.length === 2 && calls.length === 1 && puts.length === 1) {
    const kind = strikes.size === 1 ? 'Straddle' : 'Strangle'
    if (allShort) return `Short ${kind}`
    if (allLong) return `Long ${kind}`
    return null
  }

  if (open.length === 4 && calls.length === 2 && puts.length === 2) {
    const shorts = open.filter((r) => r.netQty < 0)
    const longs = open.filter((r) => r.netQty > 0)
    if (shorts.length !== 2 || longs.length !== 2) return null
    const shortStrikes = new Set(shorts.map((r) => r.strike as number))
    // Both shorts at one strike is a fly; at two, a condor.
    return shortStrikes.size === 1 ? 'Iron Fly' : 'Iron Condor'
  }

  return null
}

/** Attach marks to one projected position. */
export function toRow(p: ProjectedPosition, ltp: number | undefined): PositionRow {
  const leg: MtmLeg = {
    netQty: p.netQty,
    avgPrice: p.avgPrice,
    // Absent means no tick, which must stay distinct from zero.
    ltp: ltp !== undefined && ltp > 0 ? ltp : null,
    realisedPnl: p.realisedPnl,
    chargesTotal: p.chargesTotal,
  }
  const m = legMtm(leg)
  return { ...p, unrealised: m.unrealised, net: m.net, open: m.open }
}

/**
 * Split into groups, each one a valid payoff.
 *
 * Ordering is by the group's own exposure — most open rows first, then by
 * key — so a list does not reshuffle between polls as P&L moves.
 */
export function groupPositions(rows: readonly PositionRow[]): PortfolioGroup[] {
  const by = new Map<string, PositionRow[]>()
  for (const r of rows) {
    const underlying = r.underlying ?? UNKNOWN_UNDERLYING
    const key = `${underlying}|${r.expiry ?? ''}`
    const list = by.get(key) ?? []
    list.push(r)
    by.set(key, list)
  }

  const groups: PortfolioGroup[] = []
  for (const [key, list] of by) {
    let realised = 0
    let charges = 0
    let unrealised = 0
    let marked = 0
    let unmarked = 0
    let openRows = 0

    for (const r of list) {
      realised += r.realisedPnl
      charges += r.chargesTotal
      if (r.open) openRows += 1
      if (r.unrealised === null) {
        unmarked += 1
        continue
      }
      unrealised += r.unrealised
      marked += 1
    }

    const first = list[0]!
    const underlying = first.underlying ?? UNKNOWN_UNDERLYING
    const structure = structureNameOf(list)
    groups.push({
      key,
      underlying,
      expiry: first.expiry,
      title: structure === null ? underlying : `${underlying} ${structure}`,
      expiryLabel: expiryLabelOf(first.expiry),
      rows: list,
      realised: round2(realised),
      charges: round2(charges),
      unrealised: round2(unrealised),
      marked,
      unmarked,
      /*
       * Null, not zero, when every open row is unpriced. The realised half is
       * still on the group and still correct; a net that silently omitted the
       * open exposure would be the understatement `basketMtm` was built to
       * make impossible.
       */
      net: unmarked > 0 && marked === 0 && openRows > 0 ? null : netPnl(realised + unrealised, charges),
      openRows,
    })
  }

  return groups.sort((a, b) => b.openRows - a.openRows || a.key.localeCompare(b.key))
}

/** Rows a tab shows. */
export function forTab(rows: readonly PositionRow[], tab: PortfolioTab): PositionRow[] {
  if (tab === 'RUNNING') return rows.filter((r) => r.open)
  if (tab === 'SQUARED_OFF') return rows.filter((r) => !r.open && r.fillCount > 0)
  return [...rows]
}

/**
 * Winners and losers, by NET.
 *
 * An UNMARKED row is in neither: it is not a winner, not a loser, and not
 * zero. Hiding it under both filters would make a position disappear from
 * every view except All, which is how a real position stops being looked at.
 */
export function byResult(rows: readonly PositionRow[], filter: ResultFilter): PositionRow[] {
  if (filter === 'ALL') return [...rows]
  return rows.filter((r) => (r.net === null ? false : filter === 'WINNERS' ? r.net > 0 : r.net < 0))
}

export interface PortfolioTotals {
  /** GROSS. Always complete. */
  readonly booked: number
  readonly charges: number
  readonly unbooked: number
  readonly unmarked: number
  readonly marked: number
  /** Null when there is open exposure and nothing could be priced. */
  readonly net: number | null
}

export function totalsOf(rows: readonly PositionRow[]): PortfolioTotals {
  let booked = 0
  let charges = 0
  let unbooked = 0
  let marked = 0
  let unmarked = 0
  let openRows = 0

  for (const r of rows) {
    booked += r.realisedPnl
    charges += r.chargesTotal
    if (r.open) openRows += 1
    if (r.unrealised === null) {
      unmarked += 1
      continue
    }
    unbooked += r.unrealised
    marked += 1
  }

  booked = round2(booked)
  charges = round2(charges)
  unbooked = round2(unbooked)

  return {
    booked,
    charges,
    unbooked,
    marked,
    unmarked,
    net: unmarked > 0 && marked === 0 && openRows > 0 ? null : netPnl(booked + unbooked, charges),
  }
}

const CSV_HEADERS = [
  'underlying', 'expiry', 'strike', 'optionType', 'netQty', 'lotSize', 'avgPrice',
  'ltp', 'realisedGross', 'charges', 'unrealised', 'net', 'fills', 'tradeStatus',
] as const

/**
 * A CSV of exactly what is on screen.
 *
 * An UNMARKED cell is EMPTY, never 0. A spreadsheet sums an empty cell as
 * nothing and a zero as a value, so writing zero there would make the
 * exported total disagree with the one above the table — and the export is
 * what gets sent to an accountant.
 */
export function toCsv(rows: readonly PositionRow[], ltpOf: (token: number | null) => number | null): string {
  const cell = (v: string | number | null): string => {
    if (v === null) return ''
    const s = String(v)
    // Quote anything a spreadsheet would otherwise split or reinterpret.
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }

  const lines = [CSV_HEADERS.join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.underlying, r.expiry, r.strike, r.optionType, r.netQty, r.lotSize, r.avgPrice,
        ltpOf(r.token), r.realisedPnl, r.chargesTotal, r.unrealised, r.net, r.fillCount,
        r.tradeStatus,
      ]
        .map(cell)
        .join(','),
    )
  }
  return lines.join('\n')
}

const round2 = (v: number): number => Math.round(v * 100) / 100
